import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { publicAnnouncements } from '../services/adminService';
import { useAuth } from '../context/AuthContext';

type Announcement = {
    _id: string;
    title: string;
    body: string;
    type: 'banner' | 'popup';
    ctaLabel?: string;
    ctaUrl?: string;
};

const seenKey = (id: string) => `announcement:${id}:seen`;

/** Bloque javascript:, data:, etc. */
function safeHref(url?: string): string | null {
    if (!url || typeof url !== 'string') return null;
    const trimmed = url.trim();
    if (trimmed.startsWith('/') && !trimmed.startsWith('//')) return trimmed;
    try {
        const u = new URL(trimmed);
        if (u.protocol === 'https:' || u.protocol === 'http:') return u.toString();
    } catch {
        /* ignore */
    }
    return null;
}

function productFromPath(pathname: string): 'cv' | 'letter' | 'all' {
    if (
        pathname.startsWith('/lettre') ||
        pathname.startsWith('/creer-lettre') ||
        pathname.startsWith('/choix-modele-lettre') ||
        pathname.startsWith('/print-lettre')
    ) {
        return 'letter';
    }
    if (
        pathname.startsWith('/creer') ||
        pathname.startsWith('/modeles') ||
        pathname.startsWith('/print/')
    ) {
        return 'cv';
    }
    return 'all';
}

const AnnouncementHost: React.FC = () => {
    const { isAuthenticated } = useAuth();
    const { pathname } = useLocation();
    const [popup, setPopup] = useState<Announcement | null>(null);
    const [banner, setBanner] = useState<Announcement | null>(null);

    useEffect(() => {
        const audience = isAuthenticated ? 'users' : 'guests';
        const product = productFromPath(pathname);
        publicAnnouncements(audience, product)
            .then((list: Announcement[]) => {
                const banners = (list || []).filter((a) => a.type === 'banner');
                const popups = (list || []).filter((a) => a.type === 'popup');
                if (banners[0]) setBanner(banners[0]);
                else setBanner(null);
                const nextPopup = popups.find((p) => !localStorage.getItem(seenKey(p._id)));
                setPopup(nextPopup || null);
            })
            .catch(() => {
                /* silencieux si API down */
            });
    }, [isAuthenticated, pathname]);

    const dismissPopup = () => {
        if (popup) localStorage.setItem(seenKey(popup._id), '1');
        setPopup(null);
    };

    const bannerHref = banner ? safeHref(banner.ctaUrl) : null;
    const popupHref = popup ? safeHref(popup.ctaUrl) : null;

    return (
        <>
            {banner && (
                <div className="no-print relative z-[60] bg-indigo-700 text-white px-4 py-2.5 text-center text-sm">
                    <strong className="font-black mr-2">{banner.title}</strong>
                    <span className="opacity-90">{banner.body}</span>
                    {bannerHref && banner.ctaLabel && (
                        <a
                            href={bannerHref}
                            className="ml-3 underline font-bold"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {banner.ctaLabel}
                        </a>
                    )}
                    <button
                        type="button"
                        aria-label="Fermer"
                        className="absolute right-3 top-1/2 -translate-y-1/2 opacity-80 hover:opacity-100"
                        onClick={() => setBanner(null)}
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            {popup && (
                <div className="no-print fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative">
                        <button
                            type="button"
                            className="absolute right-3 top-3 text-slate-400 hover:text-slate-700"
                            onClick={dismissPopup}
                        >
                            <X className="w-5 h-5" />
                        </button>
                        <h3 className="text-xl font-black text-slate-900 pr-8">{popup.title}</h3>
                        <p className="text-sm text-slate-600 mt-3 leading-relaxed whitespace-pre-wrap">
                            {popup.body}
                        </p>
                        <div className="mt-5 flex gap-2 justify-end">
                            {popupHref && popup.ctaLabel && (
                                <a
                                    href={popupHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-bold"
                                    onClick={dismissPopup}
                                >
                                    {popup.ctaLabel}
                                </a>
                            )}
                            <button
                                type="button"
                                onClick={dismissPopup}
                                className="px-4 py-2 rounded-lg border text-sm font-bold text-slate-700"
                            >
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AnnouncementHost;
